import { useQuery } from "@tanstack/react-query";

import { type CustomerBundle } from "@fitness-app/data-models/entities/CustomerBundle";

import { supabase } from "~/store/initializeStore";

// Strip characters that are structural in PostgREST `or(...)` filter strings
// and escape LIKE wildcards so e.g. underscores in e-mails match literally.
const escapeIlikeEmail = (email: string) =>
  email
    .replace(/[,()]/g, "")
    .replace(/[%_]/g, (wildcard) => `\\${wildcard}`)
    .trim();

export const useCustomerBundles = (clientEmail: string | string[] | null | undefined, enabled = true) => {
  const emails = (Array.isArray(clientEmail) ? clientEmail : [clientEmail])
    .filter((email): email is string => Boolean(email))
    .map((email) => email.trim().toLowerCase())
    .filter((email, index, list) => Boolean(email) && list.indexOf(email) === index);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["customerBundles", emails],
    enabled: enabled && emails.length > 0,
    queryFn: async ({ signal }) => {
      const builder = supabase
        .from("customer_bundle")
        .select("*")
        .or(emails.map((email) => `clientEmail.ilike.${escapeIlikeEmail(email)}`).join(","))
        .order("createdAt", { ascending: true });
      const query = signal ? builder.abortSignal(signal) : builder;
      const { data, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      return (data ?? []) as unknown as CustomerBundle[];
    },
  });

  return {
    list: data ?? [],
    isLoading,
    isFetching,
  };
};
